import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  Minus,
  Activity,
  Eye,
  BarChart3,
  Search,
  Linkedin,
  Twitter,
  Sparkles,
  Radar,
  Workflow,
  Zap,
  TrendingUp,
  Quote,
  Target,
  Brain,
} from "lucide-react";
import { GlassOrb } from "@/components/glass-orb";
import { SignalChip } from "@/components/signal-chip";
import { AuroraBackground } from "@/components/aurora-background";
import { SectionEyebrow } from "@/components/section-eyebrow";
import { GradientButton } from "@/components/gradient-button";
import { ProblemSplit } from "@/components/problem-split";
import { OrbitLoop } from "@/components/orbit-loop";
import { TestimonialsMasonry, type Testimonial } from "@/components/testimonials-masonry";
import { FinalCta } from "@/components/final-cta";
import { InsideAxy, OutcomeMetricsBar } from "@/components/inside-axy";
import { previewUrl, scrollToSection, signUpUrl } from "@/lib/cta";
import { seo } from "@/lib/seo";
import { softwareApplication, faqPage, breadcrumb, reviews, webPage } from "@/lib/structured-data";
import { SITE_URL, SITE_LAST_UPDATED, FOUNDING_DATE } from "@/lib/seo-constants";

export const Route = createFileRoute("/")({
  head: () =>
    seo({
      title: "Algorithmic Marketing on Autopilot",
      description:
        "Axy.digital is the autonomous marketing engine that detects market demand and turns it into GEO, LinkedIn, and X campaigns automatically.",
      path: "/",
      type: "website",
      jsonLd: [
        softwareApplication({
          id: `${SITE_URL}/#axy-app`,
          offers: [
            {
              name: "1 week free trial",
              price: 0,
              priceCurrency: "USD",
              url: `${SITE_URL}/pricing`,
            },
            {
              name: "Agentic Marketing (yearly)",
              url: `${SITE_URL}/pricing`,
            },
            {
              name: "Agentic Marketing (monthly)",
              url: `${SITE_URL}/pricing`,
            },
          ],
        }),
        webPage({
          name: "Algorithmic Marketing on Autopilot",
          description:
            "Axy.digital is the autonomous marketing engine that detects market demand and turns it into GEO, LinkedIn, and X campaigns automatically.",
          url: `${SITE_URL}/`,
          datePublished: FOUNDING_DATE,
          dateModified: SITE_LAST_UPDATED,
        }),
        faqPage(faqs),
        ...reviews(testimonials),
        breadcrumb([{ name: "Home", url: "/" }]),
      ],
    }),
  component: LandingPage,
});

import { TrustStrip } from "@/components/trust-strip";

const flowSteps = [
  { label: "Signal", description: "Live market & search demand", icon: Radar },
  { label: "Strategy", description: "Cohesive campaign direction", icon: Brain },
  { label: "Content", description: "GEO, social, posts, threads", icon: Sparkles },
  { label: "Distribution", description: "GEO · LinkedIn · X", icon: Workflow },
  { label: "Feedback", description: "Learns from every result", icon: Activity },
];

type Cell = string | true | false;
const compareRows: { label: string; cells: [Cell, Cell, Cell, Cell] }[] = [
  {
    label: "Monthly cost",
    cells: [
      "$10,000–$20,000",
      "$3,000–$8,000",
      "$2k APIs + $15k engineer",
      "A fraction of the cost",
    ],
  },
  {
    label: "Speed to market",
    cells: ["4–6 weeks onboarding", "4–6 weeks onboarding", "6–12 weeks dev", "Instant"],
  },
  { label: "Marketing strategy", cells: [true, false, false, true] },
  { label: "Learns your brand", cells: ["Slowly", false, false, true] },
  { label: "Real-time market intelligence", cells: ["One time / static", false, false, true] },
  {
    label: "Output quality",
    cells: [
      "Human (high but slow)",
      "Human (high but slow)",
      "AI slop (generic)",
      "Data-driven (high & fast)",
    ],
  },
  { label: "Real-time analytics", cells: ["Monthly reports", "Monthly reports", false, true] },
  {
    label: "Channels supported",
    cells: ["Limited", "Specialized", "Fragmented", "GEO, SEO\u00a0"],
  },
  {
    label: "Commitment",
    cells: ["6-month contract", "Project based", "High sunk cost", "No contract"],
  },
];

const faqs = [
  {
    q: "What exactly is AI native marketing?",
    a: "Marketing built to run on AI from the ground up, not a human team bolting AI tools onto an old workflow. Research, strategy, content, and distribution are handled by the system itself, with minimal manual input at every step.",
  },
  {
    q: "What is an AI native marketing platform?",
    a: "Software that runs the entire marketing workflow autonomously, scanning the market, building strategy, creating and publishing content, and improving from results, rather than just assisting a human-run process. Axy.digital is built this way end to end.",
  },
  {
    q: "What is GEO and why does it matter?",
    a: "GEO (Generative Engine Optimization) is the practice of optimizing content to get cited by AI search engines like ChatGPT, Gemini, Perplexity, and Google AI Overviews, not just ranked in traditional search. It matters because buyers are increasingly asking AI directly instead of Googling, if you're not cited in that answer, you don't exist at the moment they're deciding.",
  },
  {
    q: "How does Axy.digital help with GEO, AI visibility, and organic traffic?",
    a: "Axy.digital runs the full SEO and GEO workflow automatically: it scans real-time search and prompt trends, builds a data-driven strategy, creates content optimized to rank on Google and get cited by AI engines, then publishes and distributes it to earn backlinks, all with minimal manual input, improving week over week.",
  },
  {
    q: "Who is Axy.digital for?",
    a: "Lean businesses and small marketing teams who want agency-level SEO and GEO results without agency headcount, timelines, or cost.",
  },
  {
    q: "How does Axy.digital listen to the market?",
    a: "Axy.digital continuously scans search trends, AI prompts, social conversations, and competitor moves relevant to your industry, surfacing what your buyers actually care about right now.",
  },
  {
    q: "Will content produced by Axy.digital sound like AI slop?",
    a: "No. Content is grounded in real-time search and market data rather than generic prompts, and it's structured specifically to meet what Google's ranking systems and AI search engines look for to cite a source, not just to read fluently.",
  },
  {
    q: "Can I review content before it goes live?",
    a: "Yes, you can review and approve content before it publishes.",
  },
  {
    q: "Does Axy.digital also get me backlinks to build domain authority?",
    a: "Yes, through Intelligent PR, an add-on that distributes unique, high-quality press releases to news publications frequently cited by AI search engines, earning you high-authority backlinks.",
  },
];

const testimonials: Testimonial[] = [
  {
    quote:
      "In 90 days, Axy.digital helped us go from invisible in AI answers to ranking #3 against incumbents with a decade of SEO dominance.",
    name: "Savannah Thatcher",
    role: "CMO",
    company: "Wingbits",
    initials: "ST",
    hue: 295,
    featured: true,
    metrics: [
      { value: "11×", label: "AI visibility growth in 90 days" },
      { value: "+30%", label: "Traffic from LLMs" },
    ],
  },
  {
    quote:
      "Axy.digital helped us 5x our content output without sounding generic. The content is detailed, sharp, and actually reflects how we want to show up.",
    name: "Aurelien Vasinis",
    role: "Founder & CEO",
    company: "Kuration AI",
    initials: "AV",
    hue: 320,
    metrics: [
      { value: "60+ hrs", label: "Saved monthly on GTM ops" },
      { value: "5×", label: "Publishing velocity" },
    ],
  },
  {
    quote:
      "The depth of the content Axy.digital generated from very little input was genuinely impressive. Not having to prompt or configure everything manually was a game changer.",
    name: "Florian Rennberger",
    role: "Head of Sales",
    company: "Conducttr Germany",
    initials: "FR",
    hue: 270,
    metrics: [
      { value: "#2 / 6", label: "AI visibility rank in Germany" },
      { value: "8 hrs", label: "Saved per week" },
    ],
  },
  {
    quote:
      "Axy.digital gave me the momentum to stay consistent. The best part is that posts crafted by Axy.digital perform 5x better.",
    name: "Oliver Jones",
    role: "Founder",
    company: "GTG Studio",
    initials: "OJ",
    hue: 305,
    metric: { value: "5×", label: "LinkedIn impressions & reach" },
  },
  {
    quote: "The content does not feel like AI, and it requires almost no editing before posting.",
    name: "Oleg Lebedev",
    role: "Founder",
    company: "Startup Marketing Consulting",
    initials: "OL",
    hue: 285,
    metric: { value: "Near-zero edits", label: "Needed before publishing" },
  },
  {
    quote:
      "Axy.digital has been incredibly helpful for non-marketers. It consistently delivers timely, well-researched content that resonates with our Web3 audience.",
    name: "William Metcalf",
    role: "Co-founder",
    company: "Curvegrid",
    initials: "WM",
    hue: 330,
    metrics: [
      { value: "20+ hrs", label: "Saved monthly on content" },
      { value: "5 hrs", label: "Saved per week" },
    ],
  },
];

function CellRender({ value }: { value: Cell }) {
  if (value === true) return <Check className="size-4 text-pink-soft" aria-label="Yes" />;
  if (value === false) return <Minus className="size-4 text-white/25" aria-label="No" />;
  return <span className="text-white/80">{value}</span>;
}

function LandingPage() {
  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative px-6 pt-20 pb-28 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <div>
            <SectionEyebrow>AI NATIVE MARKETING PLATFORM</SectionEyebrow>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-balance text-white mb-6 leading-[1.05]">
              Automate AI search visibility with{"\n"}
              <span className="text-gradient">Axy.digital</span>
            </h1>
            <p className="text-lg text-white/75 max-w-[56ch] mb-10 text-pretty">
              Axy.digital automates the full SEO and GEO workflow with minimal manual input,
              delivering agency-level outcomes at 1% of cost.{"\u00a0"}
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <GradientButton asChild>
                <a href={signUpUrl("home-hero")}>
                  Get 1 week of GEO content for free <ArrowRight className="size-4" />
                </a>
              </GradientButton>
              <GradientButton variant="ghost" onClick={() => scrollToSection("how-it-works")}>
                See how it works
              </GradientButton>
            </div>
            <div className="flex items-center gap-6 text-xs text-white/55">
              <div className="flex items-center gap-2">
                <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />
                Engine live · 1,500+ teams
              </div>
              <span>No contract · Month-to-month</span>
            </div>
          </div>

          {/* Right — orb + chips */}
          <div className="relative h-[34rem] hidden lg:block">
            <div className="absolute inset-0 grid place-items-center">
              <GlassOrb size="hero" />
            </div>
            <SignalChip
              label="Search demand rising"
              icon={TrendingUp}
              className="absolute top-6 left-2"
              delay={0}
            />
            <SignalChip
              label="Citation gap found"
              icon={Quote}
              className="absolute top-20 right-0"
              delay={1.2}
            />
            <SignalChip
              label="Competitor narrative detected"
              icon={Target}
              className="absolute bottom-32 left-0"
              delay={2}
            />
            <SignalChip
              label="Campaign opportunity found"
              icon={Zap}
              className="absolute bottom-10 right-6"
              delay={0.6}
            />
            <SignalChip
              label="AI visibility improving"
              icon={Eye}
              className="absolute top-1/2 -right-4"
              delay={3}
            />
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <TrustStrip />

      {/* PREVIEW CTA */}
      <section className="px-6 pt-16 pb-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance mb-8">
            Axy.digital delivers agency-level growth{" "}
            <span className="text-gradient">without the $10k/month retainer.</span>
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const website = new FormData(e.currentTarget).get("website") as string;
              if (!website) return;
              window.location.href = previewUrl(website, "home");
            }}
            className="glass-card flex flex-col sm:flex-row items-stretch gap-2 p-2 max-w-2xl mx-auto"
          >
            <input
              type="url"
              name="website"
              required
              placeholder="yourcompany.com"
              className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder:text-white/40 outline-none"
            />
            <GradientButton type="submit" className="shrink-0">
              See what Axy.digital can do for you <ArrowRight className="size-4" />
            </GradientButton>
          </form>
          <p className="mt-5 text-sm text-white/60 max-w-xl mx-auto">
            Axy.digital will audit your{" "}
            <Link
              to="/geo/$slug"
              params={{ slug: "what-is-ai-visibility" }}
              className="text-pink-soft underline underline-offset-2 hover:text-white transition-colors"
            >
              AI visibility
            </Link>{" "}
            and generate a report on your AI mentions, citations and GEO content that will help you
            show up for what your customers are searching for right now.{"\u00a0"}
          </p>
        </div>
        <div className="max-w-7xl mx-auto mt-12">
          <OutcomeMetricsBar />
        </div>
      </section>

      {/* PROBLEM */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>The Problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              Customers search differently every day. You{"\u00a0"}
              <span className="text-gradient">shouldn't have to guess.</span>
              {"\u00a0"}
            </h2>
          </div>
          <ProblemSplit />
        </div>
      </section>

      {/* HOW IT WORKS — flow */}
      <section className="relative px-6 py-28 overflow-hidden">
        <AuroraBackground className="opacity-50" />
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>THE AXY.DIGITAL ENGINE</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              SEO & GEO,{"\u00a0"}
              <span className="text-gradient">on autopilot.</span>
            </h2>
            <p className="mt-4 text-lg text-white/70 max-w-2xl">
              Axy.digital turns live market signals into strategy, content, and distribution, and
              gets smarter every week.
            </p>
          </div>
          <OrbitLoop steps={flowSteps} />
        </div>
      </section>

      {/* INSIDE AXY — consolidated pillars */}
      <section id="how-it-works" className="relative px-6 py-28 overflow-hidden scroll-mt-20">
        <AuroraBackground className="opacity-40" />
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <SectionEyebrow>INSIDE AXY.DIGITAL ENGINE{"\u00a0"}</SectionEyebrow>
          </div>

          <InsideAxy />
        </div>
      </section>

      {/* COMPARISON */}
      <section className="px-6 py-24">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10">
            <SectionEyebrow>How Axy.digital compares</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white">
              Agency-level growth, <span className="text-gradient">without the agency cost.</span>
            </h2>
          </div>
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th
                      scope="col"
                      className="py-4 px-4 text-left text-[10px] font-medium uppercase tracking-[0.18em] text-white/50"
                    >
                      Capability
                    </th>
                    <th
                      scope="col"
                      className="py-4 px-4 text-[10px] font-medium uppercase tracking-[0.18em] text-white/50"
                    >
                      Traditional Agency
                    </th>
                    <th
                      scope="col"
                      className="py-4 px-4 text-[10px] font-medium uppercase tracking-[0.18em] text-white/50"
                    >
                      Freelancer
                    </th>
                    <th
                      scope="col"
                      className="py-4 px-4 text-[10px] font-medium uppercase tracking-[0.18em] text-white/50"
                    >
                      DIY agents
                    </th>
                    <th
                      scope="col"
                      className="py-4 px-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-white"
                    >
                      AXY.DIGITAL ENGINE
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {compareRows.map((row) => (
                    <tr key={row.label} className="border-b border-white/5 last:border-0">
                      <th scope="row" className="py-4 px-4 text-left font-medium text-white/85">
                        {row.label}
                      </th>
                      {row.cells.slice(0, 3).map((c, j) => (
                        <td key={j} className="py-4 px-4 text-white/60">
                          <CellRender value={c} />
                        </td>
                      ))}
                      <td className="py-4 px-4 font-medium bg-gradient-to-r from-pink-soft/10 to-violet/15 text-white">
                        <CellRender value={row.cells[3]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-10 flex justify-center">
            <GradientButton asChild>
              <a href={signUpUrl("home-comparison")}>
                Get 1 week of content for free <ArrowRight className="size-4" />
              </a>
            </GradientButton>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsMasonry items={testimonials} />

      {/* FAQ */}
      <section className="px-6 py-24">
        <div className="max-w-3xl mx-auto">
          <SectionEyebrow>Questions</SectionEyebrow>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-10">
            Frequently asked
          </h2>
          <div className="glass-card p-2">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-white/10 last:border-0 px-4"
                >
                  <AccordionTrigger className="text-left text-base font-medium text-white hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 text-pretty">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCta
        heading="Stop guessing. Start getting ranked & cited."
        body="Axy.digital automates the full SEO and GEO workflow with minimal manual input, delivering agency-level outcomes at 1% of cost.\u00a0"
        primaryCta="Get 1 week of content free"
        primaryHref={signUpUrl("home-final")}
        secondaryCta="Book a demo"
        secondary={{ kind: "calendly" }}
      />

      <div className="px-6 pb-24 -mt-10">
        <div className="max-w-7xl mx-auto text-center text-sm text-white/55">
          <Link to="/products" className="text-pink-soft hover:text-white transition-colors">
            Explore all products →
          </Link>
        </div>
      </div>
    </div>
  );
}
